import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { NotebookPen, Trash2, Lock, Flame, Eye, BookOpen, CheckCircle2 } from "lucide-react";
import { MicButton } from "@/components/MicButton";
import { StoryFromJournalDialog } from "@/components/StoryFromJournal";
import { WeekReflection } from "@/components/WeekReflection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const when = (iso) => {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return days === 0 ? `Today · ${time}` : days === 1 ? `Yesterday · ${time}` : d.toLocaleDateString([], { month: "short", day: "numeric" });
};

export const VoiceJournal = ({ checkedInToday, onSaved }) => {
  const [text, setText] = useState("");
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [micState, setMicState] = useState("idle");
  const [saving, setSaving] = useState(false);
  const [week, setWeek] = useState(null);
  const [storyEntry, setStoryEntry] = useState(null);

  const load = () => {
    axios.get(`${API}/journal`, { params: { limit: 5 } }).then((r) => { setEntries(r.data.entries); setTotal(r.data.total); }).catch(() => {});
    axios.get(`${API}/journal/week`).then((r) => setWeek(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const t = text.trim();
    if (!t) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/journal`, { text: t, source: micState === "spoken" ? "voice" : "text" });
      setText("");
      setMicState("idle");
      load();
      onSaved?.(r.data.checkin);
      toast.success(r.data.checkin.status === "checked_in" ? `Saved — and that counts as grounded. Day ${r.data.checkin.streak_current}.` : "Saved. Already grounded today.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save that.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/journal/${id}`);
      setEntries((e) => e.filter((x) => x.id !== id));
      setTotal((n) => Math.max(0, n - 1));
    } catch {
      toast.error("Couldn't delete that.");
    }
  };

  return (
    <section className="tacc-card p-6 md:p-8" data-testid="voice-journal">
      <div className="flex flex-col md:flex-row md:items-start gap-6">
        <div className="flex-1 min-w-0">
          <div className="section-tag mb-2 flex items-center gap-2"><NotebookPen size={13} /> § Say how today went</div>
          <h3 className="font-display text-2xl md:text-3xl tracking-tight leading-tight">Sixty seconds. <span className="italic text-[#B45309]">Out loud.</span></h3>
          <p className="text-[#78716C] text-sm mt-2 max-w-lg">
            Tap the mic, say what today was. It's typed for you, saved as a private note, and counts as grounding.
          </p>
          <div className="flex items-center gap-2 mt-3 font-mono text-[9px] tracking-[0.2em] uppercase text-[#A8A29E]">
            <Lock size={11} /> Private — only you can ever see these
          </div>
        </div>
        <div className="flex items-center gap-4 md:pt-2">
          <MicButton
            testId="journal-mic"
            rounded
            size={64}
            maxSeconds={60}
            context="A member reflecting on how their work day went, feelings, one honest sentence."
            onText={(t) => { setText((v) => (v ? `${v} ${t}` : t)); setMicState("spoken"); }}
            onStateChange={(s) => { if (s === "recording" || s === "transcribing") setMicState(s); }}
          />
          <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#78716C] w-24" data-testid="journal-mic-hint">
            {micState === "recording" ? "Listening… tap to stop" : micState === "transcribing" ? "Typing it up…" : "Tap · talk · tap"}
          </div>
        </div>
      </div>

      <div className="mt-5">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="…or type it. One honest line is enough."
          data-testid="journal-text"
          className="w-full bg-[#FAF7F2] border border-[#E7DFD5] px-4 py-3 text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#F59E0B]/60 resize-none"
        />
        <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
          <button onClick={save} disabled={saving || !text.trim()} className="btn-ember text-xs disabled:opacity-50" data-testid="journal-save">
            <Flame size={12} /> {checkedInToday ? "Save note" : "Save & count as grounded"}
          </button>
          <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#A8A29E]">{text.length} / 2000</span>
        </div>
      </div>

      {week && (
        <div className="mt-6 pt-6 border-t border-[#E7DFD5] flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="journal-week">
          <div>
            <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#78716C] mb-2 flex items-center gap-2"><Eye size={11} /> Your week, witnessed</div>
            <div className="flex items-center gap-2">
              {week.days.map((d) => (
                <div key={d.date} className="flex flex-col items-center gap-1" title={d.date}>
                  <span className={`w-6 h-6 rounded-full border ${d.grounded ? "bg-[#F59E0B] border-[#F59E0B]" : "border-[#E7DFD5]"}`} data-testid={`week-dot-${d.date}`} />
                  <span className="font-mono text-[8px] text-[#555]">{new Date(d.date + "T12:00:00Z").toLocaleDateString([], { weekday: "short" }).slice(0, 2).toUpperCase()}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="font-display text-xl text-[#1C1917]" data-testid="journal-week-summary">
            {week.grounded_days} of 7 grounded · <span className="text-[#B45309]">{week.streak_current}-day streak</span>
            <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#A8A29E] mt-1 block">{week.notes.length} note{week.notes.length === 1 ? "" : "s"} this week · Sunday email when you showed up</div>
          </div>
        </div>
      )}

      {week && <WeekReflection canReflect={week.notes.length > 0 || week.grounded_days > 0} refreshKey={total} />}

      {entries.length > 0 && (
        <div className="mt-6 pt-6 border-t border-[#E7DFD5]" data-testid="journal-entries">
          <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#78716C] mb-3">Your last notes · {total} total</div>
          <ul className="space-y-3">
            {entries.map((e) => (
              <li key={e.id} className="group flex items-start gap-3" data-testid={`journal-entry-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#A8A29E]">{when(e.created_at)}{e.source === "voice" ? " · spoken" : ""}</div>
                  <p className="text-sm text-[#44403C] leading-relaxed mt-1 whitespace-pre-wrap">{e.text}</p>
                </div>
                {e.story_id ? (
                  <span className="inline-flex items-center gap-1 font-mono text-[9px] tracking-[0.15em] uppercase text-emerald-400 pt-2 whitespace-nowrap" title="Sent to the Story Room — pending review" data-testid={`journal-story-sent-${e.id}`}>
                    <CheckCircle2 size={12} /> Story Room
                  </span>
                ) : (
                  <button onClick={() => setStoryEntry(e)} title="Turn this note into a Story Room submission" data-testid={`journal-story-${e.id}`}
                          className="inline-flex items-center gap-1 font-mono text-[9px] tracking-[0.15em] uppercase text-[#78716C] hover:text-[#B45309] pt-2 whitespace-nowrap transition-colors">
                    <BookOpen size={12} /> Story Room
                  </button>
                )}
                <button onClick={() => remove(e.id)} aria-label="Delete note" data-testid={`journal-delete-${e.id}`} className="p-2 text-[#555] hover:text-red-400 transition-colors">
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <StoryFromJournalDialog
        entry={storyEntry}
        onClose={() => setStoryEntry(null)}
        onSent={(id, storyId) => setEntries((list) => list.map((x) => (x.id === id ? { ...x, story_id: storyId } : x)))}
      />
    </section>
  );
};
